import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { 
  Zap, Check, Crown, Users, Sparkles, 
  Clock, Shield, ArrowLeft, AlertCircle, Loader2
} from 'lucide-react';

const plans = [
  { 
    id: 'basic',
    name: 'Basic', 
    monthlyPrice: 99,
    yearlyPrice: 999,
    features: ['3 Subjects', 'Basic Quizzes (5/day)', 'Parent Dashboard', 'Chapter Essence', 'Progress Tracking', 'Exam Calendar'],
    color: 'slate',
    popular: false,
    icon: Sparkles
  },
  { 
    id: 'pro',
    name: 'Pro', 
    monthlyPrice: 299,
    yearlyPrice: 2999,
    features: ['All 8 Subjects', 'Unlimited Quizzes', 'AI Video Explainers', 'Mock Exams', 'Group Study + Chat', 'AI Chapter Chat', 'OCR Upload', 'Audio Narration', 'Priority Support'],
    color: 'violet',
    popular: true,
    icon: Crown
  },
  { 
    id: 'family',
    name: 'Family', 
    monthlyPrice: 499,
    yearlyPrice: 4999,
    features: ['Everything in Pro', 'Up to 3 Children', 'Multi-Parent Access', 'Detailed Analytics', 'WhatsApp Alerts'],
    color: 'amber',
    popular: false,
    icon: Users
  },
];

export default function SubscriptionPage({ onSuccess, onNavigate }) {
  const { api, user, refreshUser } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState('pro');
  const [billingCycle, setBillingCycle] = useState('yearly');
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [verifyingPayment, setVerifyingPayment] = useState(false);

  const currentPlan = plans.find(p => p.id === selectedPlan);
  const price = billingCycle === 'monthly' ? currentPlan?.monthlyPrice : currentPlan?.yearlyPrice;
  const monthlyEquivalent = billingCycle === 'yearly' 
    ? Math.round(currentPlan?.yearlyPrice / 12) 
    : currentPlan?.monthlyPrice;

  // Load Razorpay script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    script.onload = () => setRazorpayLoaded(true);
    script.onerror = () => setError('Failed to load payment gateway');
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const handlePaymentSuccess = useCallback(async (response, plan, billing) => {
    setVerifyingPayment(true);
    setError('');
    
    try {
      const verifyRes = await api.post('/payment/verify', {
        razorpay_order_id: response.razorpay_order_id,
        razorpay_payment_id: response.razorpay_payment_id,
        razorpay_signature: response.razorpay_signature,
        plan: plan,
        billing_cycle: billing
      });

      if (verifyRes.data.status === 'success') {
        const orderDetails = {
          plan_name: verifyRes.data.plan_name || plan,
          amount: verifyRes.data.amount_paid,
          student_name: verifyRes.data.student_name,
          billing_cycle: verifyRes.data.billing_cycle || billing,
          valid_until: verifyRes.data.valid_until,
          payment_id: response.razorpay_payment_id,
          order_id: response.razorpay_order_id
        };
        
        localStorage.setItem('studyclaw_payment_completed', 'true');
        localStorage.setItem('studyclaw_payment_data', JSON.stringify(orderDetails));
        
        if (onSuccess) {
          onSuccess('thank-you', orderDetails);
        }
        
        setTimeout(() => refreshUser(), 500);
      } else {
        setError(verifyRes.data.message || 'Payment verification failed. Please contact support.');
        setProcessing(false);
        setVerifyingPayment(false);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Payment verification failed. Please try again.');
      setProcessing(false);
      setVerifyingPayment(false);
    }
  }, [api, refreshUser, onSuccess]);

  const handleSubscribe = async () => {
    if (!razorpayLoaded) {
      setError('Payment gateway is still loading. Please wait...');
      return;
    }

    setProcessing(true);
    setError('');

    // Facebook Pixel: Track InitiateCheckout
    if (window.fbq) {
      window.fbq('track', 'InitiateCheckout', {
        content_name: selectedPlan,
        content_category: billingCycle,
        value: price,
        currency: 'INR'
      });
    }

    try {
      const orderRes = await api.post('/payment/create-order', {
        plan: selectedPlan,
        billing_cycle: billingCycle === 'yearly' ? '12months' : 'monthly'
      });

      const { order_id, amount, currency, key_id, plan_name } = orderRes.data;

      const options = {
        key: key_id,
        amount: amount,
        currency: currency,
        name: 'StudyClaw',
        description: `${plan_name} - ${billingCycle === 'yearly' ? 'Annual' : 'Monthly'}`,
        order_id: order_id,
        handler: async function(response) {
          try {
            await handlePaymentSuccess(response, selectedPlan, billingCycle === 'yearly' ? '12months' : 'monthly');
          } catch (handlerError) {
            setError('Payment succeeded but redirect failed. Please refresh the page.');
          }
        },
        prefill: {
          name: user?.name || '',
          email: user?.email || '',
        },
        theme: {
          color: '#7c3aed'
        },
        modal: {
          ondismiss: () => setProcessing(false)
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on('payment.failed', (response) => {
        setError(`Payment failed: ${response.error.description}`);
        setProcessing(false);
      });
      razorpay.open();

    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create payment order');
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 pb-24" data-testid="subscription-page">
      {/* Payment Verification Overlay */}
      {verifyingPayment && (
        <div className="fixed inset-0 bg-slate-950/95 z-50 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-violet-500/30 border-t-violet-500 rounded-full animate-spin mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">Verifying Payment...</h3>
            <p className="text-slate-400">Please wait while we confirm your payment</p>
          </div>
        </div>
      )}
      
      {/* Header */}
      <div className="sticky top-0 z-40 bg-slate-950/90 backdrop-blur-lg border-b border-slate-800/50 px-4 py-3">
        <div className="flex items-center gap-3 max-w-4xl mx-auto">
          <button 
            onClick={() => onNavigate && onNavigate('dashboard')}
            className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center hover:bg-slate-700 transition-all"
          >
            <ArrowLeft className="w-4 h-4 text-slate-400" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-white">Choose Your Plan</h1>
            <p className="text-xs text-slate-500">Start your 14-day free trial</p>
          </div>
        </div>
      </div>

      <div className="px-4 pt-6 max-w-4xl mx-auto">
        {/* Error Display */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-3"
          >
            <AlertCircle className="w-5 h-5 text-red-400 shrink-0" />
            <p className="text-sm text-red-300">{error}</p>
          </motion.div>
        )}

        {/* Billing Toggle */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center bg-slate-900/50 border border-slate-800 rounded-full p-1">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                billingCycle === 'monthly' 
                  ? 'bg-violet-600 text-white shadow-lg shadow-violet-500/20' 
                  : 'text-slate-400 hover:text-white'
              }`}
              data-testid="billing-monthly"
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                billingCycle === 'yearly' 
                  ? 'bg-violet-600 text-white shadow-lg shadow-violet-500/20' 
                  : 'text-slate-400 hover:text-white'
              }`}
              data-testid="billing-yearly"
            >
              Yearly
              <span className="text-[10px] bg-emerald-500/30 text-emerald-300 px-1.5 py-0.5 rounded-full">
                Save 20%
              </span>
            </button>
          </div>
        </div>

        {/* Plans Grid */}
        <div className="grid sm:grid-cols-3 gap-4 mb-8">
          {plans.map((plan, i) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              data-testid={`plan-card-${plan.id}`}
              onClick={() => setSelectedPlan(plan.id)}
              className={`relative rounded-2xl p-6 cursor-pointer transition-all ${
                selectedPlan === plan.id 
                  ? plan.popular
                    ? 'bg-gradient-to-b from-violet-600/20 to-slate-900/50 border-2 border-violet-500 shadow-xl shadow-violet-500/20' 
                    : 'bg-slate-900/50 border-2 border-violet-500'
                  : 'bg-slate-900/30 border border-slate-800 hover:border-slate-700'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-violet-600 text-white text-xs font-semibold px-3 py-1 rounded-full shadow-lg">
                    Most Popular
                  </span>
                </div>
              )}

              {/* Plan Icon */}
              <div className={`w-12 h-12 rounded-xl mb-4 flex items-center justify-center ${
                plan.color === 'violet' ? 'bg-violet-500/20' :
                plan.color === 'amber' ? 'bg-amber-500/20' : 'bg-slate-800'
              }`}>
                <plan.icon className={`w-6 h-6 ${
                  plan.color === 'violet' ? 'text-violet-400' :
                  plan.color === 'amber' ? 'text-amber-400' : 'text-slate-400'
                }`} />
              </div>

              <h3 className="text-lg font-bold text-white mb-1">{plan.name}</h3>
              
              {/* Price */}
              <div className="mb-4">
                <span className="text-3xl font-bold text-white">
                  ₹{billingCycle === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice}
                </span>
                <span className="text-slate-400 text-sm">
                  /{billingCycle === 'monthly' ? 'month' : 'year'}
                </span>
                {billingCycle === 'yearly' && (
                  <p className="text-xs text-slate-500 mt-1">
                    ₹{Math.round(plan.yearlyPrice / 12)}/month
                  </p>
                )}
              </div>

              {/* Features */}
              <ul className="space-y-2.5 mb-6">
                {plan.features.map((feature, j) => (
                  <li key={j} className="flex items-center gap-2 text-sm">
                    <Check className={`w-4 h-4 flex-shrink-0 ${
                      selectedPlan === plan.id ? 'text-violet-400' : 'text-slate-600'
                    }`} />
                    <span className={selectedPlan === plan.id ? 'text-slate-200' : 'text-slate-400'}>
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              {/* Selection indicator */}
              {selectedPlan === plan.id && (
                <div className="absolute top-4 right-4">
                  <div className="w-6 h-6 rounded-full bg-violet-600 flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>

        {/* Summary & CTA */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-slate-400">Selected Plan</p>
              <p className="text-lg font-bold text-white">{currentPlan?.name} - {billingCycle === 'yearly' ? 'Annual' : 'Monthly'}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-400">Total</p>
              <p className="text-2xl font-bold text-white">₹{price}</p>
            </div>
          </div>

          <button
            onClick={handleSubscribe}
            disabled={processing || !razorpayLoaded}
            className="w-full bg-gradient-to-r from-violet-600 to-violet-500 hover:from-violet-500 hover:to-violet-400 disabled:from-slate-700 disabled:to-slate-700 text-white rounded-xl py-4 font-semibold flex items-center justify-center gap-2 transition-all shadow-lg shadow-violet-500/20"
            data-testid="subscribe-btn"
          >
            {processing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Zap className="w-5 h-5" />
                Start 14-Day Free Trial
              </>
            )}
          </button>

          <div className="flex items-center justify-center gap-4 mt-4 text-xs text-slate-500">
            <div className="flex items-center gap-1">
              <Shield className="w-3.5 h-3.5" />
              Secure Payment
            </div>
            <div className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              Cancel Anytime
            </div>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="mt-8 text-center">
          <p className="text-xs text-slate-500 mb-3">Trusted by 10,000+ students across India</p>
          <div className="flex items-center justify-center gap-6 opacity-50">
            <span className="text-slate-600 text-sm font-medium">CBSE</span>
            <span className="text-slate-600 text-sm font-medium">ICSE</span>
            <span className="text-slate-600 text-sm font-medium">State Boards</span>
          </div>
        </div>
      </div>
    </div>
  );
}
